import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import DashboardLayout from '../layout/DashboardLayout';
import Alert from '../common/Alert';
import TabGroup from '../common/TabGroup';
import LoadingSpinner from '../common/LoadingSpinner';


// Service categories with their services
const SERVICE_CATEGORIES = {
  email: { name: 'Email Services', services: ['postmark'] },
  geocoding: { name: 'Geocoding', services: ['geocodio', 'here', 'radar', 'mapbox'] },
  security: { name: 'Security & Fraud', services: ['recaptcha', 'ipqualityscore', 'ip2location'] },
  crm: { name: 'CRM', services: ['hubspot'] },
  storage: { name: 'Storage', services: ['s3'] },
  cicd: { name: 'CI/CD', services: ['github_actions'] }
};

export default function ServicesUsageDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('email');
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchServicesData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/api/v1/service-usage/category/${activeTab}`);
      if (response.success) {
        setServices(response.data);
      }
    } catch (err) {
      setError('Failed to load service usage data');
      console.error('Error fetching service usage:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchServicesData();
  }, [fetchServicesData]);

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="services"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
          Service Usage Dashboard
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-2)' }}>Monitor API usage and costs for external services</p>
      </div>

      {/* Tabs */}
      <TabGroup
        tabs={Object.entries(SERVICE_CATEGORIES).map(([key, { name }]) => ({
          id: key,
          label: name,
        }))}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {/* Content */}
      {loading ? (
        <LoadingSpinner message="Loading service usage data..." />
      ) : error ? (
        <Alert variant="error" message={error} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <ServiceCard key={service.service_name} service={service} />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

function ServiceCard({ service }) {
  const {
    service_name,
    plan_name,
    today_count = 0,
    week_count = 0,
    month_count = 0,
    daily_limit,
    monthly_limit,
    cost_per_request,
    plan_url
  } = service;

  // Calculate usage percentage
  const dailyPercent = daily_limit ? (today_count / daily_limit) * 100 : 0;
  const monthlyPercent = monthly_limit ? (month_count / monthly_limit) * 100 : 0;

  // Determine meter color based on usage
  const getMeterColor = (percent) => {
    if (percent >= 90) {return 'bg-red-500';}
    if (percent >= 75) {return 'bg-yellow-500';}
    return 'bg-green-500';
  };

  // Format service name
  const formatName = (name) => {
    return name.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Calculate estimated cost
  const estimatedCost = (month_count * (cost_per_request || 0)).toFixed(2);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{formatName(service_name)}</h3>
        {plan_url && (
          <a
            href={plan_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-800 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>

      {plan_name && (
        <div className="text-sm text-gray-500 mb-4">{plan_name}</div>
      )}

      {/* Usage counts */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div>
          <div className="text-2xl font-bold text-gray-900">{today_count.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Today</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{week_count.toLocaleString()}</div>
          <div className="text-xs text-gray-500">This Week</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{month_count.toLocaleString()}</div>
          <div className="text-xs text-gray-500">This Month</div>
        </div>
      </div>

      {/* Usage meters */}
      {daily_limit && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Daily Usage</span>
            <span className="font-medium">{today_count.toLocaleString()} / {daily_limit.toLocaleString()}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getMeterColor(dailyPercent)}`}
              style={{ width: `${Math.min(dailyPercent, 100)}%` }}
            ></div>
          </div>
        </div>
      )}

      {monthly_limit && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Monthly Usage</span>
            <span className="font-medium">{month_count.toLocaleString()} / {monthly_limit.toLocaleString()}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getMeterColor(monthlyPercent)}`}
              style={{ width: `${Math.min(monthlyPercent, 100)}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Cost estimate */}
      {cost_per_request > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Estimated Monthly Cost</span>
            <span className="text-lg font-bold text-gray-900">${estimatedCost}</span>
          </div>
        </div>
      )}
    </div>
  );
}
